import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { Checkbox } from '@components/ui/checkbox';
import { FormField, FormItem, FormControl } from '@components/ui/form';
// import { Textarea } from '@components/ui/textarea';
import { HealthOption } from '@features/onboarding/components/6-health-question-form/interfaces';
import { useHealthStore } from 'src/store/health';
import { FloatingInput } from '@components/ui/input-floating';
import { UseFormReturn, Path } from 'react-hook-form';
import { FormMessageSpan } from '@components/ui/form';
import { HealthQuestionsSchema } from '@features/onboarding/schema/onboarding';
import { HIDE_DETAILS_FOR } from '@features/onboarding/components/6-health-question-form/constants';

interface FieldValue {
  value: string;
  groupKey?: string;
  details?: string;
}

interface FlatCheckboxGroupProps {
  options: (string | HealthOption)[];
  groupedOptions: HealthOption[][];
  fields: FieldValue[];
  append: (value: FieldValue) => void;
  remove: (index?: number) => void;
  questionId: string;
  errorMessage?: string | null;
  selectedOptions: string[];
  noneValue: string | undefined;
  isNoneSelected: boolean;
  visibleOptions: string[];
  form: UseFormReturn<HealthQuestionsSchema>;
  description?: string;
  setStepErrorMessage: (msg: string | null) => void;
}

export const OtherHealthConditions: React.FC<FlatCheckboxGroupProps> = ({
  options,
  groupedOptions,
  fields,
  append,
  remove,
  questionId,
  errorMessage,
  selectedOptions,
  noneValue,
  isNoneSelected,
  visibleOptions,
  form,
  description,
  setStepErrorMessage
}) => {
  const { issues, issueDetails, isFailValidation, setFailValidation } = useHealthStore();
  const prevSelectedOptions = useRef<string[]>(selectedOptions);
  const detailsRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);

  // Sort fields by their corresponding labels
  const sortedFields = React.useMemo(() => {
    return [...fields].sort((a, b) => {
      const optionA = options.find((opt) => 
        typeof opt !== 'string' && (opt as HealthOption).value === a.value
      ) as HealthOption;
      const optionB = options.find((opt) => 
        typeof opt !== 'string' && (opt as HealthOption).value === b.value
      ) as HealthOption;
      
      if (!optionA || !optionB) return 0;
      
      return optionA.label.localeCompare(optionB.label);
    });
  }, [fields, options]);

  // Easing function for smooth scrolling
  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  // Smooth scroll function
  const smoothScrollTo = useCallback((element: HTMLElement, duration: number = 800) => {
    // Check screen width to decide scrolling method
    const isLargeScreen = window.innerWidth >= 1400;
    const scrollContainer = isLargeScreen ? document.getElementById('childrenBlock') : null;
    
    const getScrollPosition = (elem: HTMLElement, container?: HTMLElement | null) => {
      let top = 0;
      let current = elem;
      while (current && (!container || current !== container)) {
        top += current.offsetTop;
        current = current.offsetParent as HTMLElement;
      }
      return top - (container ? container.offsetTop : 0) - (isLargeScreen ? 100 : 100);
    };
    
    const start = scrollContainer ? scrollContainer.scrollTop : window.pageYOffset;
    const target = getScrollPosition(element, scrollContainer);
    const distance = target - start;
    let startTime: number | null = null;
    
    const animation = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      const easeProgress = easeInOutCubic(progress);
      
      if (scrollContainer) {
        scrollContainer.scrollTop = start + distance * easeProgress;
      } else {
        window.scrollTo(0, start + distance * easeProgress);
      }
      
      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  }, []);

  const visibleDetailOptions = options.filter((option) => {
    if (typeof option === 'string') return false;
    const optionValue = option.value;
    return (
      visibleOptions.includes(optionValue) &&
      !HIDE_DETAILS_FOR.includes(optionValue)
    );
  });

  useEffect(() => {
    if (sortedFields && sortedFields.length > 0) {
      setStepErrorMessage(null);
    }
  }, [sortedFields, setStepErrorMessage]);

  useEffect(() => {
    if (questionId !== 'surgeriesDiabetesHeartMental') return;

    const conditionsPath = 'healthQuestions.medDetails.medications' as const;
    const detailsPath = 'healthQuestions.medDetails.details' as const;

    const prev = prevSelectedOptions.current;
    const added = selectedOptions.find((v) => !prev.includes(v));
    const removed = prev.find((v) => !selectedOptions.includes(v));
    const changedOption = added ?? removed;

    prevSelectedOptions.current = selectedOptions;

    if (!changedOption) return;

    if (changedOption === 'none' || selectedOptions.includes('none')) {
      form.setValue(conditionsPath, [], { shouldDirty: true, shouldValidate: true });
      form.setValue(detailsPath, {}, { shouldDirty: true, shouldValidate: true });
      return;
    }

    const groupIssue = issues.find((e) => e.key === changedOption);
    if (!groupIssue) return;

    const groupIssueId = groupIssue.id;

    const groupStillSelected = selectedOptions.some((opt) => {
      const det = issueDetails.find((d) => d.key === opt);
      return det?.issueId === groupIssueId;
    });

    if (groupStillSelected) return;

    const currentConditions: { value: string; details: string }[] = form.getValues(conditionsPath) || [];
    const currentDetails: Record<string, string> = form.getValues(detailsPath) || {};

    const groupDetails = issueDetails
      .filter((e) => e.issueId === groupIssueId)
      .map((e) => `${changedOption}-${e.key}`);

    const filteredConditions = currentConditions.filter(
      (condition) => !groupDetails.includes(condition.value)
    );

    if (filteredConditions.length !== currentConditions.length) {
      form.setValue(conditionsPath, filteredConditions, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    }

    const updatedDetails = { ...currentDetails };
    groupDetails.forEach((key) => {
      delete updatedDetails[key];
    });

    if (Object.keys(updatedDetails).length !== Object.keys(currentDetails).length) {
      form.setValue(detailsPath, updatedDetails, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    }
  }, [questionId, selectedOptions, form, issueDetails, issues]);

  // Track initial render to prevent scrolling on page load
  const isInitialRender = useRef(true);
  useEffect(() => {
    isInitialRender.current = false;
  }, []);
  
  // Do not use useEffect for scrolling to avoid unwanted scrolls
  // Scrolling is handled directly in onCheckedChange

  const detailsErrors: Record<string, string> = {};
  selectedOptions.forEach((option) => {
    if (option !== noneValue) {
      const details = form.getValues(`healthQuestions.${questionId}.details.${option}` as Path<HealthQuestionsSchema>);
      if (!details || (details as string).trim() === '') {
        detailsErrors[option] = 'Please provide details for this condition.';
      }
    }
  });

  // Auto-scroll to error message when no options are selected (including NONE APPLY)
  useEffect(() => {
    const nothingSelected = fields.length === 0 && selectedOptions.length === 0;
    if (errorMessage && nothingSelected && errorRef.current) {
      setTimeout(() => {
        smoothScrollTo(errorRef.current as HTMLElement);
      }, 100);
    }
  }, [errorMessage, fields.length, selectedOptions.length, smoothScrollTo]);

  return (
    <>
      <p className="text-[15px] lg:text-xl leading-6 lg:leading-7 mb-10 max-w-170 !font-secondary">
        {description}
      </p>
      <div
        className={`${
          ['medDetails', 'otherHealthConditions'].includes(
            questionId
          )
            ? 'grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2'
            : 'flex flex-col gap-y-2'
        }`}
      >
        {options
          ?.filter((option) => typeof option !== 'string' && !(option as HealthOption).isNoneApply)
          .map((option, index) => {
            const optionLabel = (option as HealthOption).label;
            const optionValue = (option as HealthOption).value;
            const isChecked = fields.some(
              (field: FieldValue) => field.value === optionValue
            );
            return (
              <div key={optionValue} className="flex items-center">
                <Checkbox
                  id={`${questionId}-${optionValue}`}
                  label={optionLabel}
                  checked={isChecked}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      const noneIndex = fields.findIndex(
                        (field: FieldValue) => field.value === noneValue
                      );
                      if (noneIndex !== -1) {
                        remove(noneIndex);
                      }
                      append({ value: optionValue });
                      
                      // Auto-scroll to details section only when checking a box (not unchecking)
                      if (!HIDE_DETAILS_FOR.includes(optionValue)) {
                        setTimeout(() => {
                          if (detailsRef.current) {
                            smoothScrollTo(detailsRef.current);
                          }
                        }, 300);
                      }
                    } else {
                      const indexToRemove = fields.findIndex(
                        (field: FieldValue) => field.value === optionValue
                      );
                      if (indexToRemove !== -1) {
                        remove(indexToRemove);
                      }
                    }
                  }}
                />
              </div>
            );
          })}
      </div>
      {/* NONE APPLY option below the grid */}
      {options
        ?.filter((option) => typeof option !== 'string' && (option as HealthOption).isNoneApply)
        .map((option) => {
          const optionLabel = (option as HealthOption).label;
          const optionValue = (option as HealthOption).value;
          const isChecked = fields.some(
            (field: FieldValue) => field.value === optionValue
          );
          return (
            <div key={optionValue} className="flex items-center mt-12">
              <Checkbox
                id={`${questionId}-${optionValue}`}
                label={optionLabel}
                checked={isChecked}
                onCheckedChange={(checked) => {
                  if (checked) {
                    remove();
                    append({ value: optionValue });
                  } else {
                    const indexToRemove = fields.findIndex(
                      (field: FieldValue) => field.value === optionValue
                    );
                    if (indexToRemove !== -1) {
                      remove(indexToRemove);
                    }
                  }
                }}
              />
            </div>
          );
        })}
      {errorMessage && (
        <p ref={errorRef}>
          <FormMessageSpan
            className="text-error font-extrabold mt-2 text-left"
            text={errorMessage}
          />
        </p>
      )}

      {visibleDetailOptions.length > 0 && !isNoneSelected && (
        <div ref={detailsRef} className="mt-4 border-t pt-4 space-y-4">
          {visibleDetailOptions.map((option) => {
            const healthOption = option as HealthOption;
            const optionValue = healthOption.value;
            return (
              <div key={optionValue} className="flex flex-col gap-2">
                <label className="text-sm !font-secondary">
                  Would you like to share some details about your selection of:{' '}
                  <span className="font-bold !font-secondary">
                    {healthOption.label}
                  </span>
                  ?
                </label>
                <FormField
                  control={form.control}
                  name={
                    `healthQuestions.${questionId}.details.${optionValue}` as Path<HealthQuestionsSchema>
                  }
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormControl>
                        <FloatingInput
                          {...field}
                          as="textarea"
                          id={`details-${optionValue}`}
                          value={`${field?.value ?? ''}`}
                          inputClass="border rounded-lg p-2 w-full !font-secondary"
                          label="Enter details"
                          onChange={e => {
                            field.onChange(e);
                            setFailValidation(false);
                            if (detailsErrors[optionValue]) {
                              detailsErrors[optionValue] = '';
                            }
                          }}
                        />
                      </FormControl>
                      {(isFailValidation && (fieldState.error || detailsErrors[optionValue])) && (
                        <p ref={errorRef}>
                          <FormMessageSpan
                            className="text-error font-semibold mt-2"
                            text={fieldState.error?.message || detailsErrors[optionValue]}
                          />
                        </p>
                      )}
                    </FormItem>
                  )}
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
